import mixpanel from 'mixpanel-browser';
import { createContext, ReactElement, ReactNode, useContext, useEffect, useState } from 'react';
import { CompanyType, CustomerType } from 'src/types';

type TrackingParamsType = {
  userId?: number | string;
  userEmail?: string;
  companyId?: string;
  companySlug?: string;
  [key: string]: string | string[] | undefined | number;
};

type EventParams = {
  event: string;
  eventProperties?: TrackingParamsType;
};

type TrackingIdentifyParamsType = {
  customerId: string;
  customerEmail: string;
};

type InitParamsType = {
  companySlug?: string;
  companyName?: string;
};

type AddParamsType = {
  customerEmail?: string;
};

type PeopleParamsType = {
  customerId?: string;
  customerEmail?: string;
  companySlug?: string;
  companyName?: string;
};

type TrackingProviderContextType = {
  init: (params: InitParamsType) => void;
  identify: (params: TrackingIdentifyParamsType) => void;
  addParams: (trackingParams: AddParamsType) => void;
  logEvent: (event: EventParams) => void;
};

export const TrackingProviderContext = createContext<TrackingProviderContextType>({
  init: () => {},
  identify: () => {},
  addParams: () => {},
  logEvent: () => {},
});

const TrackingProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [initialized, setInitialized] = useState(false);
  const [logEventQueue, setLogEventQueue] = useState<Array<EventParams>>([]);
  const [peopleParams, setPeopleParams] = useState<PeopleParamsType>({});

  useEffect(() => {
    logEventQueue.forEach((event) => {
      logEvent(event);
    });
  }, [initialized]);

  const init = (params: InitParamsType) => {
    if (initialized) return;

    setInitialized(true);
    setPeopleParams(params);

    if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') {
      console.log('Tracking will be initialized with params:', params);
      return;
    }

    mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_KEY, {
      track_pageview: false,
      persistence: 'localStorage',
    });
  };

  const identify = (params: TrackingIdentifyParamsType) => {
    if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') {
      console.log('Customer identified with params:', { ...params });
      return;
    }

    mixpanel.identify(params.customerId);
    mixpanel.people.set({
      $email: params.customerEmail,
    });

    setPeopleParams({ ...peopleParams, ...params });
  };

  const addParams = (params: AddParamsType) => {
    if (!params.customerEmail || process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') return;

    mixpanel.people.set({
      $email: params.customerEmail,
    });

    setPeopleParams({ ...peopleParams, ...params });
  };

  const logEvent = ({ event, eventProperties = {} }: EventParams) => {
    if (!initialized) {
      setLogEventQueue((queue) => [...queue, { event, eventProperties }]);
      return;
    }

    const peopleProperties = {
      companySlug: peopleParams.companySlug,
      companyName: peopleParams.companyName,
      userEmail: peopleParams.customerEmail,
    };

    if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production') {
      console.log('An event will be logged with params:', { event, eventProperties, peopleProperties });
      return;
    }

    mixpanel.track(event, { ...eventProperties, ...peopleProperties });
  };

  const value = {
    init,
    identify,
    addParams,
    logEvent,
  };

  return <TrackingProviderContext.Provider value={value}>{children}</TrackingProviderContext.Provider>;
};

export const useTracking = () => {
  const context = useContext(TrackingProviderContext);

  return context;
};

type TrackingInitPropsType = {
  customer?: CustomerType;
  company?: CompanyType;
};

export const TrackingInit = (props: TrackingInitPropsType) => {
  const tracking = useTracking();

  useEffect(() => {
    tracking.init({
      companyName: props.company?.name,
      companySlug: props.company?.slug,
    });

    if (props.customer) {
      tracking.identify({
        customerId: props.customer.id,
        customerEmail: props.customer.email.address,
      });
    }
  }, []);

  return null;
};

export { TrackingProvider };
